import CitationCard from "../citationcard/citationcard";
import { base } from "../../data";
import { IndexDefinition } from "../../models/indexdefinition";
import { AIChatResponseHistory } from "../../models/aichatresponsehistory";
import { CitationType } from "../../enums/CitationTypes";
import React, { useState } from "react";
import { ImageSearchResponse } from "../../models/imagesearchresponse";
import ImageList from "../imagelist/imagelist";
import FeedbackBox from "../feedbackbox/feedbackbox";
import { FeedbackType } from "../../enums/FeedbackTypes";

interface ChatQuestionCardProps {
  chatMessage: AIChatResponseHistory | null;
  streamResponse: string | null;
  streaming: boolean;
  callback: (value: string) => void;
  pinnedConversationCallback: (index: number) => void;
  saveFeedbackCallback: () => void;
  index: number;
  images?: ImageSearchResponse | null;
}

const ChatQuestionCard: React.FC<ChatQuestionCardProps> = ({
  chatMessage,
  streamResponse,
  streaming,
  callback,
  pinnedConversationCallback,
  saveFeedbackCallback,
  index,
  images,
}) => {
  const answer =
    streamResponse !== null ? streamResponse : chatMessage?.output ?? "";

  const [feedbackBoxEnabled, setFeedbackBoxEnabled] = useState<boolean>(false);
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(
    FeedbackType.None
  );
  const [feedbackReason, setFeedbackReason] = useState<string>("");
  const [feedbackMessage, setFeedbackMessage] = useState<string>("");
  const [displayFeedbackThanks, setDisplayFeedbackThanks] =
    useState<boolean>(false);

  const submitFeedback = (
    feedbackType: FeedbackType,
    feedback: string,
    feedbackMessage: string
  ) => {
    if (feedbackType === FeedbackType.Up) {
      setFeedbackType(feedbackType);

      if (chatMessage) {
        chatMessage.feedbackType = FeedbackType.Up;
        chatMessage.feedbackReason = feedback;
        chatMessage.feedbackMessage = feedbackMessage;
      }

      setDisplayFeedbackThanks(true);
      setTimeout(() => {
        setDisplayFeedbackThanks(false);
      }, 10000);
    }

    if (feedbackType === FeedbackType.Down) {
      if (chatMessage) {
        chatMessage.feedbackType = FeedbackType.Down;
        chatMessage.feedbackReason = feedback;
        chatMessage.feedbackMessage = feedbackMessage;
      }

      setFeedbackMessage(feedbackMessage);
      setFeedbackReason(feedback);
      setFeedbackType(feedbackType);
      setFeedbackBoxEnabled(false);
      setDisplayFeedbackThanks(true);
      setTimeout(() => {
        setDisplayFeedbackThanks(false);
      }, 10000);
    }

    //save feedback to db
    saveFeedbackCallback();
  };

  const extractCitations = (output: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(output, "text/html");

    // Query the document to find all 'a' tags within 'sup'
    const aTags = doc.querySelectorAll("sup > a");

    const results: { number: string; href: string; displayText: string }[] = [];

    aTags.forEach((aTag) => {
      const href = "" + aTag.getAttribute("href");
      const textContent = aTag.textContent;
      const number = "" + textContent?.replace(/[\[\]]/g, "");

      const existingResult = results.find(
        (result) => result.href === href && result.number !== number
      );

      if (existingResult) {
        existingResult.number = existingResult.number + ", " + number;
      } else {
        if (!results.find((result) => result.href === href)) {
          results.push({
            href: href,
            number: number,
            displayText: citationDisplayText(href),
          });
        }
      }
    });

    return results;
  };

  const renderFilterNames = (
    filters: string[] | undefined,
    allFilterDefinitions: IndexDefinition[]
  ) => {
    let returnValue = "All Categories";

    if (filters) {
      if (filters?.length < allFilterDefinitions.length) {
        returnValue = filters
          .map((index, i) => {
            try {
              return allFilterDefinitions.filter(
                (element) => element.indexName === index
              )[0].displayText;
            } catch (e) {
              console.log(e);
            }
          })
          .join(", ");
      }
    }

    return returnValue;
  };

  const citationDisplayText = (value: string) => {
    let returnText = value;

    const regex = RegExp(/href="([^<]+)"/, "gi");

    let m;

    do {
      m = regex.exec(returnText);

      if (m) {
        returnText = m[0].replace('href="', "").replace('/"', "");
      }
    } while (m);

    if (returnText.search("http") < 0) {
      returnText = returnText
        .split("/")
        [returnText.split("/").length - 1].replaceAll('"', "");
    }

    returnText = returnText.replace("source:", "").replace('"', "").trim();

    return returnText;
  };

  // todo: move to utils helper
  const isImage = (filename: string) => {
    const imageExtensions = [
      "jpg",
      "jpeg",
      "png",
      "gif",
      "bmp",
      "tiff",
      "webp",
    ];
    const extension = filename?.split(".")?.pop()?.toLowerCase();
    return imageExtensions.includes(extension!);
  };

  const renderCitationLinksAsNewTabLinks = (input: string) => {
    let returnValue = input;

    if (input !== undefined && input.length > 0) {
      let inputMatches;
      let urlMatches;

      const citationRegex = new RegExp(
        /<sup><a ([^<]+) target="_blank">([^<]+)<\/a><\/sup>/,
        "gi"
      );

      const urlRegex = new RegExp(/href="([^<]+)"/, "gi");

      do {
        inputMatches = citationRegex.exec(input);

        if (inputMatches) {
          do {
            urlMatches = urlRegex.exec(inputMatches[1]);
            if (urlMatches) {
              returnValue = returnValue.replace(
                inputMatches[0],
                "<a href='/citation?path=" +
                  urlMatches[1] +
                  "' target='_blank'><sup style='color: #537898'>" +
                  inputMatches[2] +
                  "</sup></a>"
              );
            }
          } while (urlMatches);
        }
      } while (inputMatches);
    }

    return returnValue;
  };

  const extractedCitations = extractCitations(
    answer.replaceAll(
      /<sup><a href="source: " target="_blank">([^<]+)<\/a><\/sup>/gi,
      ""
    )
  );

  const formatMarkdown = (value: string) => {
    const regex = new RegExp(/\*\*(.*?)\*\*/, "ig");
    return value.replace(regex, "<b>$1</b>").replace(/\n/g, "<br />");
  };

  return (
    <>
      <div className="flex min-w-0">
        <div className="flex-1">
          <h4 className="text-red font-semibold">
            {chatMessage && chatMessage.question}
          </h4>
        </div>
        <div className="flex-none">
          {displayFeedbackThanks && (
            <div className="bg-white shadow-lg border border-[#F2F2F2] py-2 px-3 fixed top-10 left-[50%]">
              <div className="flex flex-row gap-2">
                <div className="flex">
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="stroke-current stroke-0 m-auto"
                  >
                    <path
                      d="M8.99935 0.666992C4.39935 0.666992 0.666016 4.40033 0.666016 9.00033C0.666016 13.6003 4.39935 17.3337 8.99935 17.3337C13.5993 17.3337 17.3327 13.6003 17.3327 9.00033C17.3327 4.40033 13.5993 0.666992 8.99935 0.666992ZM8.99935 15.667C5.32435 15.667 2.33268 12.6753 2.33268 9.00033C2.33268 5.32533 5.32435 2.33366 8.99935 2.33366C12.6743 2.33366 15.666 5.32533 15.666 9.00033C15.666 12.6753 12.6743 15.667 8.99935 15.667ZM12.8243 5.31699L7.33268 10.8087L5.17435 8.65866L3.99935 9.83366L7.33268 13.167L13.9993 6.50033L12.8243 5.31699Z"
                      fill="#218000"
                    />
                  </svg>
                </div>
                <div>Thanks for your feedback</div>
              </div>
            </div>
          )}

          {!streaming && (
            <div className="flex flex-row gap-2">
              {feedbackBoxEnabled && (
                <FeedbackBox
                  setFeedbackBoxEnabled={setFeedbackBoxEnabled}
                  callback={submitFeedback}
                />
              )}
              <div className="my-auto">
                <button
                  type="button"
                  disabled={chatMessage!.feedbackType !== FeedbackType.None}
                  className={
                    chatMessage!.feedbackType === FeedbackType.Up
                      ? "flex w-[32px] h-[32px] bg-light-blue"
                      : "flex w-[32px] h-[32px] hover:bg-light-blue"
                  }
                  onClick={() => submitFeedback(FeedbackType.Up, "", "")}
                >
                  <svg
                    width="20"
                    height="18"
                    viewBox="0 0 20 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="stroke-current stroke-0 m-auto"
                  >
                    <path
                      d="M7.49967 17.3337H14.9997C15.6913 17.3337 16.283 16.917 16.533 16.317L19.0497 10.442C19.1247 10.2503 19.1663 10.0503 19.1663 9.83366V8.16699C19.1663 7.25033 18.4163 6.50033 17.4997 6.50033H12.2413L13.033 2.69199L13.058 2.42533C13.058 2.08366 12.9163 1.76699 12.6913 1.54199L11.808 0.666992L6.31634 6.15866C6.01634 6.45866 5.83301 6.87533 5.83301 7.33366V15.667C5.83301 16.5837 6.58301 17.3337 7.49967 17.3337ZM7.49967 7.33366L11.1163 3.71699L9.99967 8.16699H17.4997V9.83366L14.9997 15.667H7.49967V7.33366ZM0.833008 7.33366H4.16634V17.3337H0.833008V7.33366Z"
                      fill="#3D5F7B"
                    />
                  </svg>
                </button>
              </div>
              <div className="my-auto">
                <button
                  type="button"
                  disabled={chatMessage!.feedbackType !== FeedbackType.None}
                  className={
                    chatMessage!.feedbackType === FeedbackType.Down
                      ? "flex w-[32px] h-[32px] bg-light-blue"
                      : "flex w-[32px] h-[32px] hover:bg-light-blue"
                  }
                  onClick={() => setFeedbackBoxEnabled(true)}
                >
                  <svg
                    width="20"
                    height="18"
                    viewBox="0 0 20 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="stroke-current stroke-0 m-auto"
                  >
                    <path
                      d="M12.4997 0.666992H4.99967C4.30801 0.666992 3.71634 1.08366 3.46634 1.68366L0.949674 7.55866C0.874674 7.75033 0.833008 7.95033 0.833008 8.16699V9.83366C0.833008 10.7503 1.58301 11.5003 2.49967 11.5003H7.75801L6.96634 15.3087L6.94134 15.5753C6.94134 15.917 7.08301 16.2337 7.30801 16.4587L8.19134 17.3337L13.683 11.842C13.983 11.542 14.1663 11.1253 14.1663 10.667V2.33366C14.1663 1.41699 13.4163 0.666992 12.4997 0.666992ZM12.4997 10.667L8.88301 14.2837L9.99967 9.83366H2.49967V8.16699L4.99967 2.33366H12.4997V10.667ZM15.833 0.666992H19.1663V10.667H15.833V0.666992Z"
                      fill="#3D5F7B"
                    />
                  </svg>
                </button>
              </div>
              <div>
                <button
                  type="button"
                  className={
                    chatMessage && chatMessage.pin
                      ? "flex w-[32px] h-[32px] border border-blue text-white bg-blue "
                      : "flex w-[32px] h-[32px] border border-blue text-blue hover:bg-blue hover:text-white"
                  }
                  onClick={() => pinnedConversationCallback(index)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="stroke-current stroke-0 m-auto"
                    width="10"
                    height="14"
                    fill="currentColor"
                    viewBox="0 0 10 14"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1"
                      d="M6.33398 1.66667V5C6.33398 5.74667 6.58065 6.44 7.00065 7H3.00065C3.43398 6.42667 3.66732 5.73334 3.66732 5V1.66667H6.33398ZM8.33398 0.333336H1.66732C1.30065 0.333336 1.00065 0.633336 1.00065 1C1.00065 1.36667 1.30065 1.66667 1.66732 1.66667H2.33398V5C2.33398 6.10667 1.44065 7 0.333984 7V8.33334H4.31398V13L4.98065 13.6667L5.64732 13V8.33334H9.66732V7C8.56065 7 7.66732 6.10667 7.66732 5V1.66667H8.33398C8.70065 1.66667 9.00065 1.36667 9.00065 1C9.00065 0.633336 8.70065 0.333336 8.33398 0.333336Z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="text-xs">
        <div className="flex flex-row flex-wrap gap-x-2">
          Filters:&nbsp;
          {chatMessage && renderFilterNames(chatMessage.indexes, base)}
        </div>
      </div>
      <div>
        <h6 className="text-red font-semibold flex py-4">
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="my-auto mr-2"
          >
            <path
              d="M5.22094 11.1125C5.42511 11.2567 5.66844 11.3333 5.91761 11.3325V11.3292C6.15287 11.3298 6.38312 11.2612 6.57974 11.132C6.77635 11.0028 6.93065 10.8187 7.02344 10.6025L7.53428 9.04917C7.65219 8.69725 7.8504 8.3776 8.1132 8.11552C8.37601 7.85343 8.6962 7.65611 9.04844 7.53917L10.5301 7.0575C10.7692 6.97407 10.9758 6.81723 11.1205 6.60939C11.2651 6.40155 11.3404 6.15332 11.3356 5.90014C11.3308 5.64695 11.2462 5.40176 11.0937 5.19956C10.9413 4.99735 10.7289 4.84847 10.4868 4.77417L9.02428 4.29917C8.67059 4.18128 8.34923 3.98261 8.08568 3.71891C7.82213 3.45522 7.62364 3.13375 7.50594 2.78L7.02428 1.29917C6.94028 1.06331 6.78489 0.85948 6.57972 0.715992C6.37455 0.572505 6.12978 0.496488 5.87942 0.498504C5.62906 0.50052 5.38555 0.580469 5.18272 0.727242C4.97988 0.874016 4.8278 1.08032 4.74761 1.3175L4.26178 2.81083C4.14446 3.1543 3.95077 3.46668 3.69529 3.72448C3.4398 3.98227 3.12917 4.17876 2.78678 4.29917L1.30594 4.77417C1.06827 4.85641 0.862338 5.01105 0.717083 5.21636C0.571829 5.42167 0.494554 5.66733 0.496117 5.91883C0.49768 6.17032 0.578003 6.41501 0.725798 6.6185C0.873594 6.82199 1.08143 6.97406 1.32011 7.05333L2.78178 7.5275C3.13542 7.64622 3.45665 7.84547 3.72014 8.10954C3.98362 8.3736 4.18217 8.69527 4.30011 9.04917L4.78178 10.5283C4.86428 10.7642 5.01761 10.9683 5.22094 11.1125ZM4.90928 7.57C4.81646 7.44848 4.71539 7.33348 4.60678 7.22583H4.60928C4.20674 6.82329 3.71601 6.52001 3.17594 6.34L1.84261 5.91667L3.18511 5.48C3.71639 5.29605 4.19825 4.99239 4.59344 4.5925C4.98688 4.19003 5.28354 3.7033 5.46094 3.16917L5.89011 1.85083L6.32011 3.17333C6.49989 3.71231 6.80301 4.20187 7.20535 4.60305C7.60768 5.00423 8.09812 5.30594 8.63761 5.48417L9.98761 5.91917L8.66261 6.34833C8.12402 6.5275 7.63463 6.82982 7.23334 7.23125C6.83205 7.63269 6.52992 8.12218 6.35094 8.66083L5.92178 9.97917L5.49261 8.65833C5.36109 8.26519 5.16385 7.89719 4.90928 7.57ZM11.6101 15.3175C11.4511 15.2042 11.3316 15.0439 11.2684 14.8592L10.9951 14.0208C10.9421 13.8613 10.8527 13.7162 10.734 13.5972C10.6152 13.4781 10.4704 13.3884 10.3109 13.335L9.48511 13.0658C9.2962 13.0018 9.13196 12.8805 9.0153 12.7187C8.89863 12.5569 8.83535 12.3628 8.83428 12.1633C8.83404 11.965 8.89586 11.7716 9.01108 11.6102C9.12631 11.4488 9.28915 11.3275 9.47678 11.2633L10.3151 10.9917C10.471 10.9363 10.6122 10.8458 10.7278 10.7274C10.8433 10.6089 10.9302 10.4656 10.9818 10.3083L11.2518 9.4825C11.3153 9.29564 11.4353 9.13312 11.5952 9.01741C11.7551 8.90171 11.947 8.83853 12.1443 8.8366C12.3417 8.83468 12.5348 8.89411 12.6969 9.00669C12.859 9.11926 12.9821 9.27941 13.0493 9.465L13.3234 10.31C13.3771 10.4684 13.4668 10.6122 13.5853 10.7302C13.7039 10.8482 13.8481 10.9371 14.0068 10.99L14.8334 11.2592C15.024 11.3188 15.191 11.4368 15.3107 11.5967C15.4304 11.7565 15.4967 11.9499 15.5003 12.1495C15.5039 12.3492 15.4446 12.5449 15.3307 12.7089C15.2168 12.8729 15.0542 12.9969 14.8659 13.0633L14.0209 13.3375C13.8621 13.3911 13.7178 13.4809 13.5995 13.5998C13.4812 13.7186 13.3921 13.8634 13.3393 14.0225L13.0709 14.8467C13.0059 15.0383 12.8826 15.205 12.7168 15.3217C12.555 15.4367 12.3613 15.4981 12.1628 15.4974C11.9643 15.4966 11.771 15.4337 11.6101 15.3175ZM10.7518 12.165C11.0788 12.2822 11.3756 12.471 11.6204 12.7175C11.8652 12.964 12.0519 13.2621 12.1668 13.59C12.2829 13.2635 12.4703 12.9669 12.7153 12.7219C12.9604 12.4768 13.2569 12.2894 13.5834 12.1733C13.2538 12.0579 12.9541 11.8702 12.7063 11.6239C12.4586 11.3776 12.2691 11.079 12.1518 10.75C12.0371 11.0748 11.852 11.3702 11.6097 11.615C11.3673 11.8598 11.0738 12.0479 10.7501 12.1658"
              fill="#FF372F"
            />
          </svg>
          Answer
        </h6>
      </div>
      <div
        dangerouslySetInnerHTML={{
          __html: formatMarkdown(
            renderCitationLinksAsNewTabLinks(
              answer
                .replaceAll("\n", "<br />")
                .replaceAll("\n\n", "<br /><br />")
                .replaceAll(
                  /<sup><a href="source" target="_blank">([^<]+)<\/a><\/sup>/gi,
                  ""
                )
            )
          ),
        }}
      />

      {extractedCitations.length > 0 && (
        <div>
          <h6 className="text-red font-semibold flex py-4">
            <svg
              width="18"
              height="14"
              viewBox="0 0 18 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="my-auto mr-2"
            >
              <path
                d="M15.667 1.99998H9.00033L7.33366 0.333313H2.33366C1.41699 0.333313 0.675326 1.08331 0.675326 1.99998L0.666992 12C0.666992 12.9166 1.41699 13.6666 2.33366 13.6666H15.667C16.5837 13.6666 17.3337 12.9166 17.3337 12V3.66665C17.3337 2.74998 16.5837 1.99998 15.667 1.99998ZM15.667 12H2.33366V1.99998H6.64199L8.30866 3.66665H15.667V12ZM14.0003 6.99998H4.00033V5.33331H14.0003V6.99998ZM10.667 10.3333H4.00033V8.66665H10.667V10.3333Z"
                fill="#FF372F"
              />
            </svg>
            Citation
          </h6>
        </div>
      )}

      {extractedCitations.length > 0 && (
        <div className="flex gap-4 flex-wrap min-w-0">
          {extractedCitations?.map((item, i) => {
            return (
              !isImage(item.href) && (
                <CitationCard
                  citationType={CitationType.Document}
                  citation={item.href}
                  docPath={null}
                  displayText={item.displayText}
                  index={item.number}
                  callback={callback}
                />
              )
            );
          })}
        </div>
      )}

      {images && <ImageList images={images} callback={callback} />}
    </>
  );
};

export default ChatQuestionCard;
